import type { PlacedSegment } from './timeline.ts';

/**
 * Detected pauses, placed on the clock.
 *
 * The backend reports them as offsets into a segment's file; the editor works
 * in absolute time, so they are moved onto the same axis as everything else the
 * moment they arrive.
 */

export interface Gap {
  fromMs: number;
  toMs: number;
}

/** How near a released handle has to be for a gap to claim it. */
export const SNAP_RADIUS_MS = 400;

export interface RawGaps {
  segment_id: number;
  gaps: Array<{ fromSec: number; toSec: number }>;
}

export function placeGaps(raw: RawGaps, segment: PlacedSegment): Gap[] {
  return raw.gaps
    .map((g) => ({
      fromMs: segment.fileStartMs + g.fromSec * 1000,
      toMs: segment.fileStartMs + g.toSec * 1000,
    }))
    // Only pauses inside the audio this segment actually contributes; the head
    // of a file after a reconnect duplicates the previous segment.
    .filter((g) => g.toMs > segment.fromMs && g.fromMs < segment.toMs);
}

export function midpointOf(gap: Gap): number {
  return (gap.fromMs + gap.toMs) / 2;
}

/**
 * The gap close enough to claim a released handle, or null.
 *
 * Snapping to the middle rather than an edge: the middle of a pause is the
 * safest place to cut, far enough from both the word before and the word after
 * that neither is clipped.
 */
export function snapTarget(
  atMs: number,
  gaps: Gap[],
  radiusMs = SNAP_RADIUS_MS,
): Gap | null {
  let best: Gap | null = null;
  let bestDistance = Infinity;

  for (const gap of gaps) {
    // Inside the pause already counts as no distance at all.
    const distance = atMs < gap.fromMs
      ? gap.fromMs - atMs
      : atMs > gap.toMs
        ? atMs - gap.toMs
        : 0;
    if (distance <= radiusMs && distance < bestDistance) {
      bestDistance = distance;
      best = gap;
    }
  }
  return best;
}

/**
 * Moves both edges of a range inward to the nearest pause, for "trim silence at
 * the edges". A selection dragged roughly usually opens and closes inside a
 * pause; this drops that dead air without touching the speech.
 */
export function trimToSpeech(
  fromMs: number,
  toMs: number,
  gaps: Gap[],
): { fromMs: number; toMs: number } {
  let start = fromMs;
  let end = toMs;

  for (const gap of gaps) {
    // A pause the selection opens inside: start where it ends.
    if (gap.fromMs <= start && gap.toMs > start && gap.toMs < end) start = gap.toMs;
    // A pause the selection closes inside: end where it begins.
    if (gap.toMs >= end && gap.fromMs < end && gap.fromMs > start) end = gap.fromMs;
  }
  return end > start ? { fromMs: start, toMs: end } : { fromMs, toMs };
}
